export function createFukushizaiRepresentativeProjectionService(deps) {
    const {
        selectedFukushizaiContextRef,
        fukushizaiRepresentativeRowRef,
        fukushizaiDetailSyncingRef,
        fukushizaiGroupClearedByUserRef,
        selectedSetItem,
        selectedGroup,
        FUKUSHIZAI_BASE_PANEL_TYPE,
        isEmpty,
        isEnabled,
        DicVal,
        normalizeFukushizaiContext,
        createEmptyFukushizaiContext,
        normalizeGroupNo,
        readRowValueIgnoreCase,
        readFukushizaiContextFromRow,
        getItemCdFromRow,
        getColorCdFromRow,
        getGroupNoFromRow,
        sameItemGroupColor,
        resolveFukushizaiContext,
        hasCompleteFukushizaiContext,
        countTraceObjectKeys,
        getDetailRowsByPanelType,
        getFukushizaiAllFieldIds,
        updateSelectedFukushizaiContext,
        setSelectedSetItem,
        setSelectedGroup,
        runBatched,
        createTimingCollector,
        clearFukushizaiMergedFields,
        setFukushizaiEntryProjectionContext,
        activateFukushizaiProjectionGuard,
        mergeQualityRepresentativeRowToEntry,
        logEntryVisualTrace
    } = deps;

    // context から副資材 113 行の空データを生成します。
    function createFukushizaiEmptyRow(context, rowid) {
        const resolved = normalizeFukushizaiContext(context);
        return {
            rowid: String(rowid ?? "0"),
            SSDZ_ITMCITEMCD: resolved.itemCd,
            SSDZ_COLORCD: resolved.colorCd,
            SSDZ_FUKUSIZAICOLORGRP: resolved.groupNo
        };
    }

    // item/group/color に一致する代表行 index を解決します。
    function findFukushizaiRepresentativeRowIndex(rows, context) {
        const list = Array.isArray(rows) ? rows : [];
        const resolved = resolveFukushizaiContext(list, context);
        if (list.length === 0) return -1;
        if (resolved.itemCd === "" || resolved.groupNo === "") return -1;

        // color まで一致する行があれば最優先で採用します。
        if (resolved.colorCd !== "") {
            const exactIndex = list.findIndex((row) => {
                if (isEmpty(row)) return false;
                return sameItemGroupColor(row, resolved.itemCd, resolved.groupNo, resolved.colorCd);
            });
            if (exactIndex !== -1) return exactIndex;
        }

        return list.findIndex((row) => {
            if (isEmpty(row)) return false;
            const itemCd = String(readRowValueIgnoreCase(row, "SSDZ_ITMCITEMCD") ?? "").trim();
            const groupNo = normalizeGroupNo(readRowValueIgnoreCase(row, "SSDZ_FUKUSIZAICOLORGRP"));
            return itemCd === resolved.itemCd && groupNo === resolved.groupNo;
        });
    }

    // 現在 context に対応する代表行を取得し、必要なら空行も返します。
    function pickFukushizaiRepresentativeRow(rows, context = selectedFukushizaiContextRef.current, allowCreate = false) {
        const list = Array.isArray(rows) ? rows.filter((row) => !isEmpty(row) && typeof row === "object") : [];
        const resolved = resolveFukushizaiContext(list, context);

        if (list.length === 0) {
            if (!allowCreate) return { row: null, index: -1, created: false, context: resolved };
            return { row: createFukushizaiEmptyRow(resolved, "0"), index: 0, created: true, context: resolved };
        }

        let hitIndex = findFukushizaiRepresentativeRowIndex(list, resolved);
        if (hitIndex === -1 && allowCreate) {
            const nextRow = createFukushizaiEmptyRow(resolved, String(list.length));
            list.push(nextRow);
            hitIndex = list.length - 1;
            return { row: nextRow, index: hitIndex, created: true, context: resolved };
        }

        if (hitIndex === -1) {
            return { row: null, index: -1, created: false, context: resolved };
        }

        return {
            row: list[hitIndex],
            index: hitIndex,
            created: false,
            context: readFukushizaiContextFromRow(list[hitIndex])
        };
    }

    // detail 113 から初期表示用の副資材 context を解決します。
    function resolveInitialFukushizaiContextFromDetailRows(rows) {
        const list = Array.isArray(rows) ? rows : [];
        const first = list.find((row) => {
            if (isEmpty(row)) return false;
            return getItemCdFromRow(row) !== "" && getGroupNoFromRow(row) !== "" && getColorCdFromRow(row) !== "";
        });
        return isEmpty(first) ? createEmptyFukushizaiContext() : readFukushizaiContextFromRow(first);
    }

    // 選択中の副資材代表行を entrydata へ投影します。
    function mergeFukushizaiRepresentativeRowToEntry(entryObj, detailData, contextOverride = null, options = {}) {
        const timing = createTimingCollector("mergeFukushizaiRepresentativeRowToEntry");
        if (isEmpty(entryObj) || isEmpty(detailData)) return false;

        const beforeKeyCount = countTraceObjectKeys(entryObj);
        const rows = timing.measure("getRows", () => getDetailRowsByPanelType(detailData, FUKUSHIZAI_BASE_PANEL_TYPE));
        const currentContext = timing.measure("normalizeContext", () => normalizeFukushizaiContext(contextOverride || selectedFukushizaiContextRef.current));
        const allowInitialFallback = isEnabled(DicVal(options, "allowInitialFallback", false));
        const updateSelection = isEnabled(DicVal(options, "updateSelection", false));

        let picked = timing.measure("pickRepresentative", () => pickFukushizaiRepresentativeRow(rows, currentContext, false));

        // 初回ロードでは context 未確定でも最初の有効行を暫定採用します。
        if ((isEmpty(picked) || isEmpty(picked.row))
            && allowInitialFallback
            && !hasCompleteFukushizaiContext(currentContext)
            && !fukushizaiGroupClearedByUserRef.current) {
            const fallbackIndex = timing.measure("findFallbackIndex", () => rows.findIndex((row) => !isEmpty(row)));
            if (fallbackIndex >= 0) {
                picked = {
                    row: rows[fallbackIndex],
                    index: fallbackIndex,
                    created: false,
                    context: readFukushizaiContextFromRow(rows[fallbackIndex])
                };
            }
        }

        if (isEmpty(picked) || isEmpty(picked.row)) {
            // 副資材行が無い場合でも品質表示側の投影は維持します。
            if (rows.length === 0) {
                timing.measure("clearMergedFieldsEmpty", () => {
                    clearFukushizaiMergedFields(entryObj);
                    mergeQualityRepresentativeRowToEntry(entryObj, detailData, currentContext);
                });
                timing.flush({
                    rowCount: rows.length,
                    result: true,
                    reason: "empty-rows",
                    updateSelection
                });
                logEntryVisualTrace("mergeFukushizaiRepresentativeRowToEntry:emptyRows", {
                    context: currentContext,
                    rowCount: rows.length,
                    beforeKeyCount,
                    afterKeyCount: countTraceObjectKeys(entryObj),
                    updateSelection
                });
                return true;
            }

            fukushizaiRepresentativeRowRef.current = { rowid: "", index: -1 };
            timing.measure("clearMergedFieldsNoPick", () => {
                clearFukushizaiMergedFields(entryObj);
                mergeQualityRepresentativeRowToEntry(entryObj, detailData, currentContext);
            });
            timing.flush({
                rowCount: rows.length,
                result: false,
                reason: "no-picked-row",
                updateSelection
            });
            logEntryVisualTrace("mergeFukushizaiRepresentativeRowToEntry:noPickedRow", {
                context: currentContext,
                rowCount: rows.length,
                beforeKeyCount,
                afterKeyCount: countTraceObjectKeys(entryObj),
                updateSelection
            });
            return false;
        }

        const row = picked.row;
        const rowId = String(DicVal(row, "rowid", picked.index) ?? picked.index);
        fukushizaiRepresentativeRowRef.current = { rowid: rowId, index: picked.index };
        const nextContext = timing.measure("readNextContext", () => readFukushizaiContextFromRow(row));

        if (updateSelection) {
            timing.measure("updateSelectionState", () => {
                runBatched(() => {
                    updateSelectedFukushizaiContext(nextContext);
                    if (selectedSetItem !== nextContext.itemCd) setSelectedSetItem(nextContext.itemCd);
                    if (selectedGroup !== nextContext.groupNo) setSelectedGroup(nextContext.groupNo);
                });
            });
        }

        const previousSyncing = fukushizaiDetailSyncingRef.current;
        fukushizaiDetailSyncingRef.current = true;
        timing.measure("activateProjectionGuard", () => {
            activateFukushizaiProjectionGuard();
        });

        try {
            timing.measure("clearMergedFields", () => {
                clearFukushizaiMergedFields(entryObj);
            });

            const fieldIds = timing.measure("getAllFieldIds", () => getFukushizaiAllFieldIds());
            timing.measure("copyRepresentativeFields", () => {
                fieldIds.forEach((fieldId) => {
                    if (Object.prototype.hasOwnProperty.call(row, fieldId)) {
                        entryObj[fieldId] = row[fieldId];
                    }
                    [`${fieldId}_cd`, `${fieldId}_CD`, `${fieldId}_nm`, `${fieldId}_NM`].forEach((displayFieldId) => {
                        if (Object.prototype.hasOwnProperty.call(row, displayFieldId)) {
                            entryObj[displayFieldId] = row[displayFieldId];
                        }
                    });
                });
            });

            // 副資材 context を保持した後に品質表示側も同じ context で再投影します。
            timing.measure("mergeProjectionContextAndQuality", () => {
                setFukushizaiEntryProjectionContext(entryObj, nextContext);
                mergeQualityRepresentativeRowToEntry(entryObj, detailData, nextContext);
            });
        } finally {
            fukushizaiDetailSyncingRef.current = previousSyncing;
        }

        timing.flush({
            rowCount: rows.length,
            pickedIndex: picked.index,
            pickedRowId: rowId,
            result: true,
            updateSelection
        });
        logEntryVisualTrace("mergeFukushizaiRepresentativeRowToEntry:applied", {
            context: nextContext,
            rowCount: rows.length,
            pickedIndex: picked.index,
            pickedRowId: rowId,
            beforeKeyCount,
            afterKeyCount: countTraceObjectKeys(entryObj),
            updateSelection
        });
        return true;
    }

    return {
        createFukushizaiEmptyRow,
        findFukushizaiRepresentativeRowIndex,
        pickFukushizaiRepresentativeRow,
        resolveInitialFukushizaiContextFromDetailRows,
        mergeFukushizaiRepresentativeRowToEntry
    };
}
